import { useEffect, useMemo, useState } from "react";
import { X, Loader2, Search, ShieldCheck, UserPlus, Upload, Check, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminTerritoriesMap } from "@/components/AdminTerritoriesMap";
import { AdminModalOverlay, AdminModalCard, AdminModalBody } from "@/components/AdminModal";

const SITUATION_OPTIONS = {
  installe: "Déjà installé / entreprise existante",
  en_creation: "Entreprise en cours de création",
  a_creer: "Souhaite créer son activité",
};
const EXPERIENCE_OPTIONS = {
  aucune: "Aucune", moins_2_ans: "Moins de 2 ans", "2_5_ans": "2 à 5 ans",
  "5_10_ans": "5 à 10 ans", plus_10_ans: "Plus de 10 ans",
};
const TERRITORY_STATUS_LABELS = { disponible: "Disponible", reserve: "Réservé", attribue: "Actif" };
const TERRITORY_STATUS_CLS = {
  disponible: "bg-sage/20 text-navy", reserve: "bg-amber-100 text-amber-700", attribue: "bg-red-100 text-red-600",
};
const DOCUMENT_LABELS = { identity: "Pièce d'identité", kbis: "KBIS", rc_pro: "Attestation RC Pro" };

const computePricePreview = (n) => {
  if (n <= 0) return 0;
  let price = 249;
  if (n >= 2) price += 179;
  if (n >= 3) price += 99 * (n - 2);
  return price;
};

const EMPTY_FORM = {
  firstname: "", lastname: "", email: "", phone: "", company_name: "", siret: "",
  address: "", postal_code: "", city: "", situation: "installe", experience: "aucune", message: "",
};

const territoryLabel = (t) => (
  t.partner ? `${t.partner.firstname} ${t.partner.lastname}${t.partner.company_name ? ` (${t.partner.company_name})` : ""}` : null
);

export const AdminCreatePartnerModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [territories, setTerritories] = useState([]);
  const [loadingTerritories, setLoadingTerritories] = useState(true);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [pickerMode, setPickerMode] = useState("recherche");
  const [primary, setPrimary] = useState(null);
  const [extraIds, setExtraIds] = useState([]);
  const [subscriptionType, setSubscriptionType] = useState("STANDARD");
  const [exemptionReason, setExemptionReason] = useState("");
  const [activateNow, setActivateNow] = useState(false);
  const [files, setFiles] = useState({ identity: null, kbis: null, rc_pro: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/territories")
      .then((res) => setTerritories(res.data))
      .catch(() => toast.error("Impossible de charger les territoires V4"))
      .finally(() => setLoadingTerritories(false));
  }, []);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2 || territories.length === 0) { setResults([]); return; }
    let active = true;
    const local = territories.filter((t) => {
      const partnerText = territoryLabel(t) || "";
      return `${t.code} ${t.name} ${t.department} ${partnerText}`.toLowerCase().includes(q.toLowerCase());
    });
    const params = /^\d{2,5}$/.test(q) ? { cp: q.padEnd(5, "") } : { q };
    const timer = setTimeout(() => {
      api.get("/communes/search", { params }).then((res) => {
        if (!active) return;
        const codes = new Set((res.data || []).map((c) => c.insee_code));
        const communeMatches = territories.filter((t) => (t.commune_insee_codes || []).some((c) => codes.has(c)));
        const merged = [...local, ...communeMatches].filter((t, i, arr) => arr.findIndex((x) => x.id === t.id) === i);
        setResults(merged.slice(0, 15));
      }).catch(() => { if (active) setResults(local.slice(0, 15)); });
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [term, territories]);

  const selectPrimary = (t) => {
    if (t.status !== "disponible") {
      toast.error(`Territoire déjà occupé — ${territoryLabel(t) || "un partenaire"} — impossible de l'attribuer silencieusement`);
      return;
    }
    setPrimary(t);
    setExtraIds([]);
    setTerm("");
    setResults([]);
  };

  const neighborOptions = useMemo(
    () => (primary ? territories.filter((t) => (primary.neighbor_ids || []).includes(t.id)) : []),
    [primary, territories]
  );

  const toggleExtra = (t) => {
    if (t.status !== "disponible") return;
    setExtraIds((prev) => (prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id]));
  };

  const territoryCount = primary ? 1 + extraIds.length : 0;
  const pricePreview = subscriptionType === "EXONERE" ? 0 : computePricePreview(territoryCount);

  const submit = async () => {
    if (!form.firstname.trim() || !form.lastname.trim() || !form.email.trim() || !form.phone.trim() || !form.postal_code.trim() || !form.city.trim()) {
      toast.error("Prénom, nom, email, téléphone, code postal et ville sont requis");
      return;
    }
    if (!primary) {
      toast.error("Sélectionnez un territoire principal (V4)");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("primary_territory_id", primary.id);
      fd.append("extra_territory_ids", extraIds.join(","));
      fd.append("subscription_type", subscriptionType);
      fd.append("exemption_reason", exemptionReason);
      fd.append("activate_immediately", activateNow ? "true" : "false");
      Object.entries(files).forEach(([k, f]) => { if (f) fd.append(k, f); });
      const res = await api.post("/admin/partners", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(
        res.data.login_email_sent
          ? "Partenaire créé — email d'accès envoyé"
          : "Partenaire créé (email d'accès non envoyé — vérifiez sa configuration)"
      );
      if (res.data.login_warning) toast.error(res.data.login_warning);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de la création du partenaire");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModalOverlay testId="admin-create-partner-modal" onClose={onClose}>
      <AdminModalCard maxWidth="max-w-2xl">
        <div className="flex shrink-0 items-start justify-between border-b border-slate-100 px-7 py-6 sm:px-9">
          <div>
            <h2 className="flex items-center gap-2 font-display text-2xl font-medium text-navy">
              <UserPlus className="h-5 w-5 text-cognac" /> Ajouter un partenaire
            </h2>
            <p className="mt-1 text-xs text-slate-400">Création directe depuis la Centrale, sans passer par la candidature publique.</p>
          </div>
          <button data-testid="create-partner-close" onClick={onClose}
            className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors duration-300 hover:bg-navy hover:text-white" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>

        <AdminModalBody>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Responsable</p>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <input data-testid="create-partner-firstname" placeholder="Prénom" value={form.firstname}
              onChange={(e) => setForm({ ...form, firstname: e.target.value })}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
            <input data-testid="create-partner-lastname" placeholder="Nom" value={form.lastname}
              onChange={(e) => setForm({ ...form, lastname: e.target.value })}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
            <input data-testid="create-partner-email" type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
            <input data-testid="create-partner-phone" placeholder="Téléphone" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
            <input data-testid="create-partner-company" placeholder="Nom de l'entreprise" value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
            <input data-testid="create-partner-siret" placeholder="SIRET" value={form.siret}
              onChange={(e) => setForm({ ...form, siret: e.target.value })}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
            <input data-testid="create-partner-address" placeholder="Adresse" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="col-span-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
            <input data-testid="create-partner-postal" placeholder="Code postal" value={form.postal_code}
              onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
            <input data-testid="create-partner-city" placeholder="Ville" value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
            <select data-testid="create-partner-situation" value={form.situation} onChange={(e) => setForm({ ...form, situation: e.target.value })}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-cognac focus:outline-none">
              {Object.entries(SITUATION_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select data-testid="create-partner-experience" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-cognac focus:outline-none">
              {Object.entries(EXPERIENCE_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <textarea data-testid="create-partner-message" placeholder="Informations complémentaires (facultatif)" rows={2} value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />

          <p className="mt-6 text-xs font-bold uppercase tracking-wider text-slate-400">Territoire principal (référentiel V4)</p>
          {primary ? (
            <div data-testid="create-partner-primary-selected" className="mt-2 rounded-xl border border-cognac/40 bg-cream p-3.5">
              <p className="text-xs font-semibold text-slate-500">Territoire principal sélectionné</p>
              <div className="mt-1.5 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-navy">
                    <span className="font-mono">{primary.code}</span> — {primary.name}
                  </p>
                  <p className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                    {primary.department} · {primary.commune_count} communes ·{" "}
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${TERRITORY_STATUS_CLS[primary.status]}`}>
                      {TERRITORY_STATUS_LABELS[primary.status]}
                    </span>
                  </p>
                </div>
                <button type="button" data-testid="create-partner-primary-clear" onClick={() => { setPrimary(null); setExtraIds([]); }}
                  className="shrink-0 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-500 hover:border-navy hover:text-navy">
                  Changer
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <div className="mb-3 inline-flex rounded-full border border-slate-200 p-1">
                <button type="button" data-testid="create-partner-mode-search" onClick={() => setPickerMode("recherche")}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${pickerMode === "recherche" ? "bg-navy text-white" : "text-slate-500"}`}>
                  <Search className="h-3.5 w-3.5" /> Recherche
                </button>
                <button type="button" data-testid="create-partner-mode-map" onClick={() => setPickerMode("carte")}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${pickerMode === "carte" ? "bg-navy text-white" : "text-slate-500"}`}>
                  <MapIcon className="h-3.5 w-3.5" /> Carte
                </button>
              </div>

              {pickerMode === "recherche" ? (
                <>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="create-partner-territory-search"
                      value={term}
                      onChange={(e) => setTerm(e.target.value)}
                      placeholder={loadingTerritories ? "Chargement des territoires…" : "Commune, code postal, nom ou code CH-xx-xx…"}
                      disabled={loadingTerritories}
                      className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-4 text-sm focus:border-cognac focus:outline-none disabled:opacity-50"
                    />
                  </div>
                  {results.length > 0 && (
                    <div className="mt-2 max-h-56 space-y-1.5 overflow-y-auto">
                      {results.map((t) => (
                        <button
                          type="button"
                          key={t.id}
                          data-testid={`create-partner-territory-result-${t.code}`}
                          onClick={() => selectPrimary(t)}
                          className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 px-3.5 py-2.5 text-left text-sm transition-colors hover:border-cognac"
                        >
                          <span>
                            <span className="font-semibold text-navy">{t.name}</span>{" "}
                            <span className="font-mono text-xs text-slate-400">({t.code})</span>
                            <span className="block text-xs text-slate-400">{t.department}</span>
                          </span>
                          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${TERRITORY_STATUS_CLS[t.status]}`}>
                            {TERRITORY_STATUS_LABELS[t.status]}{territoryLabel(t) ? ` — ${territoryLabel(t)}` : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div data-testid="create-partner-territory-map">
                  {loadingTerritories ? (
                    <p className="py-8 text-center text-sm text-slate-400">Chargement de la carte V4…</p>
                  ) : (
                    <>
                      <p className="mb-2 text-xs text-slate-400">Cliquez sur un territoire disponible pour le sélectionner comme territoire principal.</p>
                      <AdminTerritoriesMap territories={territories} onOpenTerritory={selectPrimary} />
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {primary && neighborOptions.length > 0 && (
            <div className="mt-4" data-testid="create-partner-extra-territories">
              <p className="text-xs font-semibold text-slate-500">Territoires supplémentaires (limitrophes, facultatif) :</p>
              <div className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
                {neighborOptions.map((t) => (
                  <label key={t.id} data-testid={`create-partner-extra-${t.code}`}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${t.status !== "disponible" ? "cursor-not-allowed border-slate-100 opacity-50" : "cursor-pointer border-slate-200"}`}>
                    <input type="checkbox" disabled={t.status !== "disponible"} checked={extraIds.includes(t.id)} onChange={() => toggleExtra(t)} className="h-4 w-4 accent-cognac" />
                    {t.name} ({t.code}) — <span className={`font-bold ${t.status === "disponible" ? "text-sage" : "text-red-500"}`}>{TERRITORY_STATUS_LABELS[t.status]}</span>
                    {territoryLabel(t) ? ` (${territoryLabel(t)})` : ""}
                  </label>
                ))}
              </div>
            </div>
          )}

          <p className="mt-6 text-xs font-bold uppercase tracking-wider text-slate-400">Abonnement</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" data-testid="create-partner-sub-standard" onClick={() => setSubscriptionType("STANDARD")}
              className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${subscriptionType === "STANDARD" ? "border-navy bg-navy text-white" : "border-slate-200 text-slate-600"}`}>
              Standard — tarif calculé
            </button>
            <button type="button" data-testid="create-partner-sub-exonere" onClick={() => setSubscriptionType("EXONERE")}
              className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${subscriptionType === "EXONERE" ? "border-sage bg-sage/20 text-navy" : "border-slate-200 text-slate-600"}`}>
              <ShieldCheck className="h-3.5 w-3.5" /> Exonéré — 0 €
            </button>
          </div>
          {subscriptionType === "EXONERE" && (
            <input data-testid="create-partner-exemption-reason" value={exemptionReason} onChange={(e) => setExemptionReason(e.target.value)}
              placeholder="Motif interne (ex. Compte interne PME CozyHome)"
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          )}
          <p className="mt-2 text-sm text-slate-500" data-testid="create-partner-price-preview">
            Tarif mensuel HT calculé : <span className="font-bold text-navy">{pricePreview} € HT / mois</span>
            {territoryCount > 0 ? ` pour ${territoryCount} territoire(s)` : ""}
          </p>

          <p className="mt-6 text-xs font-bold uppercase tracking-wider text-slate-400">Documents (facultatif à la création)</p>
          <div className="mt-2 space-y-2">
            {Object.entries(DOCUMENT_LABELS).map(([key, label]) => (
              <label key={key} data-testid={`create-partner-doc-${key}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm">
                <span className="flex items-center gap-2 text-navy">
                  <Upload className="h-3.5 w-3.5 text-slate-400" /> {label}
                  {files[key] && <Check className="h-3.5 w-3.5 text-sage" />}
                </span>
                <input type="file" accept="application/pdf,image/jpeg,image/png" className="max-w-[160px] text-xs"
                  onChange={(e) => setFiles((prev) => ({ ...prev, [key]: e.target.files?.[0] || null }))} />
              </label>
            ))}
            <p className="text-[11px] text-slate-400">Les documents non fournis resteront « À fournir » ; le partenaire pourra les compléter depuis son espace.</p>
          </div>

          <label className="mt-6 flex items-center gap-2.5 rounded-xl border border-slate-200 bg-cream px-4 py-3 text-sm font-semibold text-navy">
            <input type="checkbox" data-testid="create-partner-activate-now" checked={activateNow} onChange={(e) => setActivateNow(e.target.checked)}
              className="h-4 w-4 accent-cognac" />
            Activer immédiatement le territoire
          </label>
          <p className="mt-1.5 text-[11px] text-slate-400">
            {activateNow
              ? "Le partenaire sera créé ACTIF et le(s) territoire(s) immédiatement attribué(s)."
              : "Le partenaire sera créé en attente de souscription — vous pourrez confirmer le paiement puis activer le territoire depuis sa fiche."}
          </p>
        </AdminModalBody>

        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-100 px-7 py-5 sm:px-9">
          <button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-600 hover:border-navy hover:text-navy">
            Annuler
          </button>
          <button type="button" data-testid="create-partner-submit" disabled={saving} onClick={submit}
            className="flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Créer le partenaire
          </button>
        </div>
      </AdminModalCard>
    </AdminModalOverlay>
  );
};
